(function () {
  'use strict';
  const Base = customElements.get('evertee-owned-bundle');
  if (!Base) return;
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  function totalAfterDiscount(gross, discount, rate) {
    if (!integer(gross) || gross <= 0 || gross > 100000000 || !integer(discount) ||
        !Number.isFinite(rate) || rate < 0.000001 || rate > 100000) throw new Error('pricing');
    const converted = Math.round(discount * rate);
    if (!integer(converted) || converted > 100000000) throw new Error('pricing');
    const raw = Math.max(0, gross - converted);
    if (raw === gross) return gross;
    let total = Math.ceil(raw / 100) * 100;
    if (total > gross && raw > 100) total = Math.ceil((raw - 100) / 100) * 100;
    return total > gross ? raw : total;
  }
  class EverteeMigratedBundle extends Base {
    exchangeRate() {
      const currency = window.Shopify?.currency;
      const rate = Number(currency?.rate);
      if (currency?.active !== this.dataset.currency || !Number.isFinite(rate) || rate < 0.000001 ||
          rate > 100000 || (currency.active === 'GBP' && rate !== 1)) throw new Error('pricing');
      return rate;
    }

    readPricingState(value) {
      if (value?.active !== true || value.version !== 'MIGRATION:1' || value.productId !== this.dataset.productId ||
          value.currency !== this.dataset.currency || typeof value.revision !== 'string' || !value.revision ||
          value.revision.length > 128 || /[|\r\n]/.test(value.revision) ||
          !same(value.discounts, this.catalog.discounts)) throw new Error('pricing');
      return {active:true,version:value.version,productId:value.productId,currency:value.currency,
        revision:value.revision,discounts:value.discounts,rate:this.exchangeRate()};
    }

    ownedPricingActive() {
      return this.dataset.ownedPricingActive === 'true' && this.dataset.ownedPricingVersion === 'MIGRATION:1' &&
        this.catalog?.pricing?.engine === 'evertee-migration-v1' && this.pricingState?.active === true;
    }

    freePremiumActive() { return false; }

    async prepareCatalog(catalog) {
      this.pendingKey = 'evertee-migration-pending-v1:' + this.dataset.productId;
      if (!Array.isArray(catalog.discounts) || !same(catalog.discounts.map(rule => rule.count), [1,3,5,10]) ||
          catalog.discounts.some(rule => !integer(rule.amount)) ||
          catalog.variants.some(variant => !integer(variant.basePrice) || variant.basePrice <= 0)) throw new Error('pricing');
      const main = ['9886457856266','10043716010250','10385313759498','10580231258378'];
      if (this.dataset.currency === 'GBP' && main.includes(this.dataset.productId)) throw new Error('pricing');
      this.pricingState = this.readPricingState(JSON.parse(this.querySelector('[data-owned-pricing-state]').textContent));
      const representatives = [...new Map(catalog.variants.map(variant => [variant.basePrice,variant])).values()];
      const units = new Map();
      for (const variant of representatives) {
        const current = await this.readVariant(variant);
        if (this.dataset.currency === 'GBP' && current.price !== variant.basePrice) throw new Error('pricing');
        units.set(variant.basePrice,current.price);
      }
      catalog.variants = catalog.variants.map(variant => ({...variant,price:units.get(variant.basePrice)}));
      catalog.currency = this.dataset.currency;
      catalog.pricing = {...catalog.pricing,currency:catalog.currency,verified:true,engine:'evertee-migration-v1',endsAt:null,
        unitCentsByFinish:Object.fromEntries(catalog.finishes.map(finish => [finish,catalog.variants.find(v => v.options[2] === finish).price])),
        discountCentsByCount:Object.fromEntries(catalog.discounts.map(rule => [rule.count,rule.amount]))};
    }

    async readVariant(variant, signal) {
      const url = new URL(this.dataset.productUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('variant-url');
      url.searchParams.set('variant',variant.id);
      url.searchParams.set('section_id',this.dataset.variantSection);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id',preview);
      const response = await fetch(url.href,{credentials:'same-origin',signal,cache:'no-store'});
      if (!response.ok) throw new Error('variant-fetch');
      const document = new DOMParser().parseFromString(await response.text(),'text/html');
      const node = document.querySelector('[data-et-owned-selected-variant]');
      if (!node) throw new Error('variant-data');
      const current = JSON.parse(node.textContent);
      this.assertCurrentPricing(current.ownedPricing);
      if (String(current.productId) !== this.dataset.productId || current.currency !== this.dataset.currency ||
          !current.variant || String(current.variant.id) !== String(variant.id) ||
          typeof current.variant.available !== 'boolean' || !integer(current.variant.price) || current.variant.price <= 0) throw new Error('variant-mismatch');
      return {...current.variant,id:String(current.variant.id),options:variant.options};
    }

    async fetchVariant(variant, signal) {
      const current = await this.readVariant(variant,signal);
      if (current.price !== variant.price) throw new Error('pricing-changed');
      return current;
    }

    makeBundle(variants) {
      const bundle = super.makeBundle(variants);
      bundle.setProperties({...window.EverteeBundleCore.bundleProperties(this.pending.id,this.count),_et_bundle_version:'MIGRATION:1'});
      return bundle;
    }

    finishTotal(count, finish) {
      const size = this.size || this.sizes[0];
      const gross = Array.from({length:count},(_,index) => {
        const colour = this.rows[index] || this.catalog.initialColour;
        const variant = this.catalog.variants.find(v => v.options[0] === colour && v.options[1] === size && v.options[2] === finish);
        if (!variant) throw new Error('variant');
        return variant.price;
      }).reduce((sum,price) => sum + price,0);
      const rule = this.pricingState.discounts.find(rule => rule.count === count);
      if (!rule) throw new Error('pricing');
      return totalAfterDiscount(gross,rule.amount,this.pricingState.rate);
    }

    cardTotal(count) { return this.finishTotal(count,this.finish); }

    pricingTotal(bundle, variants) {
      if (!this.ownedPricingActive() || this.dataset.pricingReady !== 'true' || this.exchangeRate() !== this.pricingState.rate) throw new Error('pricing');
      const prices = new Map(variants.map(variant => [variant.id,variant.price]));
      const gross = bundle.resolve().items.reduce((sum,line) => sum + prices.get(line.id) * line.quantity,0);
      const rule = this.pricingState.discounts.find(rule => rule.count === this.count);
      const total = totalAfterDiscount(gross,rule.amount,this.pricingState.rate);
      if (total !== this.cardTotal(this.count)) throw new Error('pricing-changed');
      return total;
    }

    renderFinish() {
      super.renderFinish();
      const node = this.querySelector('[data-owned-premium-price]');
      if (!node || !this.finishMap.premiumBlank) return;
      const suffix = this.logo ? 'Logo' : 'Blank';
      const extra = this.finishTotal(this.count,this.finishMap['premium'+suffix]) - this.finishTotal(this.count,this.finishMap['standard'+suffix]);
      node.textContent = this.say('premiumExtra').replaceAll('__ET_PRICE__',this.money(extra));
    }

    async refresh() {
      try {
        if (this.catalog && this.pricingState) {
          this.renderCounts();
          this.renderRows();
          this.renderFinish();
        }
      } catch (error) { this.lock(); this.purchaseError(error); return false; }
      return super.refresh();
    }
  }
  if (!customElements.get('evertee-migrated-bundle')) customElements.define('evertee-migrated-bundle',EverteeMigratedBundle);
})();
